/**
 * The notification bell and its panel.
 *
 * Everything shown here comes from the server. There is no seeded list, no
 * example row and no optimistic count: an empty inbox renders as empty,
 * because a bell that shows three imaginary items is worse than no bell.
 *
 * Delivery is polling, deliberately. The only thing that produces a
 * notification today is a reconstruction finishing minutes after it was
 * started, so a request every half minute is the whole requirement - and SSE
 * or a WebSocket would add a connection to authenticate, keep alive and
 * reconnect for a feature whose events arrive that rarely. That trade is
 * written down in the ADR rather than left implicit.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/api/client";
import type { Notification } from "@/api/types";
import { paths } from "@/app/paths";
import { reconstructionFailureMessage } from "@/i18n/terms";
import "./notifications.css";

/** Slow on purpose: the events behind this take minutes to happen. */
const POLL_MS = 30_000;

const TONES: Record<string, string> = {
  reconstruction_completed: "ok",
  reconstruction_failed: "warn",
};

/**
 * What a finished run is called, in the words the person who started it used.
 *
 * A success says what it produced. Both a sparse check and a surface run
 * succeed, and `has_model` - written by the backend from a validated GLB, the
 * same predicate the reconstruction screen reads - is what separates them.
 * Telling somebody their model is ready when it is a point cloud is the one
 * sentence this bell most needs not to send. When the flag is absent (an older
 * notification), it says the neutral true thing rather than guessing.
 */
function notificationTitle(item: Notification): string {
  if (item.kind === "reconstruction_failed") return "3D oluşturma tamamlanamadı";
  if (item.kind !== "reconstruction_completed") return "Bildirim";
  const model = item.metadata?.has_model;
  if (model === "true") return "3D modeliniz hazır";
  if (model === "false") return "3D kontrol tamamlandı";
  return "3D çalışmanız tamamlandı";
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 1) return "az önce";
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  return new Date(iso).toLocaleDateString("tr-TR");
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<Notification[] | null>(null);
  const [failed, setFailed] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async (signal?: AbortSignal) => {
    try {
      const { unread: count } = await getUnreadNotificationCount(
        signal ? { signal } : {},
      );
      setUnread(count);
      setFailed(false);
    } catch {
      // A failed count is not worth a visible error: the badge simply does
      // not move. The panel says something when it is opened.
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refreshCount(controller.signal);
    // One timer, and no overlapping requests: the interval is far longer than
    // a request takes, and the abort below stops the last one on unmount.
    const timer = window.setInterval(() => void refreshCount(controller.signal), POLL_MS);
    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  async function togglePanel() {
    const next = !open;
    setOpen(next);
    if (!next) return;
    setItems(null);
    try {
      setItems(await listNotifications({ limit: 20 }));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }

  async function openItem(item: Notification) {
    // Opening marks *this one* read. Opening the panel does not mark
    // everything read - that would erase the state the user came to check.
    if (!item.read_at) {
      try {
        await markNotificationRead(item.id);
        setItems((current) =>
          (current ?? []).map((row) =>
            row.id === item.id ? { ...row, read_at: new Date().toISOString() } : row,
          ),
        );
        await refreshCount();
      } catch {
        setFailed(true);
        return;
      }
    }
    if (item.project_id) {
      setOpen(false);
      // Reconstruction notifications belong on the reconstruction screen -
      // the place that can show what finished or why it did not.
      navigate(paths.projectReconstruction(item.project_id));
    }
  }

  async function readAll() {
    try {
      const { unread: count } = await markAllNotificationsRead();
      setUnread(count);
      setItems(await listNotifications({ limit: 20 }));
    } catch {
      // The server said no, so nothing is claimed to have changed.
      setFailed(true);
    }
  }

  return (
    <div className="bell" ref={root}>
      <button
        type="button"
        className="bell__button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          unread > 0 ? `Bildirimler, ${unread} okunmamış` : "Bildirimler"
        }
        onClick={() => void togglePanel()}
      >
        <span aria-hidden="true">🔔</span>
        {unread > 0 && (
          <span className="bell__badge" aria-hidden="true">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="bell__panel" role="dialog" aria-label="Bildirimler">
          <div className="bell__head">
            <strong>Bildirimler</strong>
            {unread > 0 && (
              <button type="button" className="bell__link" onClick={() => void readAll()}>
                Tümünü okundu yap
              </button>
            )}
          </div>

          {items === null && !failed && <p className="bell__muted">Yükleniyor…</p>}

          {failed && (
            <p className="bell__muted" role="alert">
              Bildirimler şu anda yüklenemiyor.
            </p>
          )}

          {items !== null && items.length === 0 && !failed && (
            <p className="bell__muted">
              Henüz bildiriminiz yok. Önemli analiz ve işlem sonuçları burada
              görünecek.
            </p>
          )}

          {items !== null && items.length > 0 && (
            <ul className="bell__list">
              {items.map((item) => {
                const tone = TONES[item.kind] ?? "info";
                // The same sentences the reconstruction screen shows, from the
                // one map. A second copy here had already drifted into
                // "Seyrek 3B oluşturulamadı" and a code the backend does not
                // emit.
                const detail = item.metadata?.failure_code
                  ? reconstructionFailureMessage(item.metadata.failure_code)
                  : undefined;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`bell__item${item.read_at ? "" : " bell__item--unread"}`}
                      onClick={() => void openItem(item)}
                    >
                      <span className={`bell__dot bell__dot--${tone}`} aria-hidden="true" />
                      <span className="bell__body">
                        <span className="bell__title">
                          {notificationTitle(item)}
                          {!item.read_at && <span className="bell__new"> · yeni</span>}
                        </span>
                        {detail && <span className="bell__detail">{detail}</span>}
                        <span className="bell__time">{relativeTime(item.created_at)}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
